import axios from "axios";
import { API_BASE_PATH, API_PATH } from "../utils/config";
import {
  buildExpiryLoginRedirect,
  readAuthStore,
} from "../utils/loginRedirect";
import {
  isEmailUnverifiedError,
  showEmailUnverifiedCue,
} from "../utils/emailUnverifiedCue";
import { v4 as uuidv4 } from "uuid"; // Importing uuid package

const getBaseUrl = () => {
  let baseUrl = API_BASE_PATH;
  if (
    typeof process !== "undefined" &&
    process?.env?.NODE_ENV === "development"
  ) {
    baseUrl = API_BASE_PATH;
  }
  return baseUrl;
};

const instance = axios.create({
  baseURL: getBaseUrl(),
});

const notLoginCall = (config) => {
  return config.url !== API_PATH.LOGIN;
};

instance.interceptors.request.use(
  (request) => {
    // console.log(request)
    if (!request.headers.Authorization) {
      const token = JSON.parse(localStorage.getItem("keyCloak") || "{}").token;
      if (token) {
        request.headers.Authorization = `Token ${token}`;
      }
    }

    //  Add TRANSACTION-ID header with a random number
    const transactionId = uuidv4(); // Generate a unique UUID
    request.headers["TRANSACTION-ID"] = transactionId;

    return request;
  },
  (error) => {
    console.log(error);
    return Promise.reject(error);
  },
  { runWhen: notLoginCall }
);

instance.interceptors.response.use(
  (response) => {
    if (
      response?.config?.url === API_PATH.LOGIN ||
      response?.config?.url === API_PATH.GOOGLE_LOGIN ||
      response?.config?.url === API_PATH.FACEBOOK_LOGIN ||
      response?.config?.url === API_PATH.APPLE_LOGIN ||
      response?.config?.url === API_PATH.MAGIC_LINK_REDEEM ||
      // FEAT-L9: passkey authentication_verify returns the SAME {token, user}
      // shape as password/magic-link login (see backend
      // PasskeyAuthenticationVerifyViewSet doc comment) -- persist it here too.
      response?.config?.url === API_PATH.PASSKEY_AUTHENTICATION_VERIFY
    ) {
      let token = response?.data?.token;
      let user = response?.data?.user || JSON.parse(localStorage.getItem("keyCloak")).user;
      localStorage.setItem("keyCloak", JSON.stringify({ token, user }));
    }
    // console.log(response)
    return response;
  },
  (error) => {
    console.log("error", error);
    if (
      error.response &&
      error.response.status === 401 &&
      error.response.data.detail === "Invalid token."
    ) {
      // BUG #700 generic mid-session expiry (Gordon 2026-08-23): capture WHO was
      // signed in BEFORE clearing the store. A 401 "Invalid token." means a
      // token WAS sent and the server rejected it — i.e. a session that was
      // authenticated has just expired mid-use — so we (almost always) know the
      // user's email here. Passing it to buildExpiryLoginRedirect routes the
      // user to the explained re-auth banner (reason=session-expired + email
      // prefill) instead of a bare /login with no explanation. If no email is
      // remembered, the helper falls back to the same return-to login URL this
      // block used before (byte-identical for that case).
      const expiredEmail = readAuthStore().user?.email || "";
      localStorage.removeItem("keyCloak");
      localStorage.removeItem("persist:root"); // EW-1004: clear persisted redux remnant on forced logout
      window.location.href =
        window.location.origin + buildExpiryLoginRedirect(expiredEmail);
    }
    // error.response.status === 403 && error.response.data.detail === "CSRF Failed: CSRF token missing or incorrect."
    // ISP-9999 fe-unverified-banner-and-cues: every gated action (purchase,
    // publish/share, chat send, invite, email change, referral, username set,
    // clone) shares this one axios instance, so this is the single place that
    // catches the backend's 403 email_unverified refusal and shows a visible
    // cue instead of the action just failing silently. Behind
    // UNVERIFIED_GUARDRAILS_ENABLED only in spirit — inert in practice while
    // the backend flag is off, since that 403 code is never returned then.
    if (isEmailUnverifiedError(error)) {
      showEmailUnverifiedCue();
    }
    return Promise.reject(error);
  }
);

export default instance;
